import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import Loader from "../component/Loader";
import { BASE_URL, LIVE_TRADES_SOCKET_URL } from "../api";
import { fetchRealAccounts } from "../hooks/useRealAccounts";
import { exportToExcel } from "../utils/exportData";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import CommonTable from "../component/CommonTable";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";
import { usePolling } from "../hooks/usePolling";

/**
 * LIVE UPDATES:
 * 1. Socket (/CRM_OPEN_ORDER_LIST): after connecting, { login: <account> } is sent and the server pushes that account's open trades.
 * 2. API fallback: while the socket is not delivering trades (cannot connect, closed, or silent),
 *    the open trades API is called every LIVE_TRADES_REFRESH_MS instead. Set it to 0 to turn this off.
 */
const LIVE_TRADES_REFRESH_MS = 5000;
// Socket is treated as "not working" when it sends nothing for this long (API fallback starts again)
const LIVE_TRADES_SOCKET_SILENCE_MS = 15000;
// Wait before reconnecting a closed socket: first value, doubled after every failure up to the max
const LIVE_TRADES_SOCKET_RETRY_MS = 5000;
const LIVE_TRADES_SOCKET_RETRY_MAX_MS = 60000;

// Only the account type(s) offered on this page. Demo is switched off for now:
// to enable it again, uncomment the "Demo" <option> in both Account Type dropdowns below.
const DEFAULT_ACCOUNT_TYPE = "live";

// Keeps real trade objects only, ignores MT5 error codes like ["-106"] or ["-1"]
const isValidTrade = (item) =>
  item &&
  typeof item === "object" &&
  (item.Position !== undefined ||
    item.Ticket !== undefined ||
    item.Symbol !== undefined ||
    item.Login !== undefined ||
    item.Account !== undefined);

// Reads one socket message. Returns the list of open trades, or null when the message is not a trade list.
const parseSocketTrades = (rawMessage, account) => {
  let message;
  try {
    message = JSON.parse(rawMessage);
  } catch (error) {
    return null;
  }
  if (!message) return null;

  const list = Array.isArray(message)
    ? message
    : Array.isArray(message.data)
      ? message.data
      : Array.isArray(message.positions)
        ? message.positions
        : null;
  if (!list) return null;

  // Message for another account (when the server says which account it belongs to)
  const messageAccount = message.account_number ?? message.login ?? message.loginid;
  if (messageAccount !== undefined && messageAccount !== null && String(messageAccount) !== String(account)) {
    return null;
  }

  return list.filter(isValidTrade);
};

// Prices are shown with the symbol's own number of decimals (API "Digits"), e.g. 4183.5 -> 4183.50
const formatPrice = (value, digits) => {
  if (value === null || value === undefined || value === "" || value === "-") return "-";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const d = Number(digits);
  return Number.isInteger(d) && d >= 0 && d <= 10 ? num.toFixed(d) : String(value);
};

// MT5 sends 0 when no Take Profit / Stop Loss is set
const formatTpSl = (value, digits) => {
  if (value === null || value === undefined || value === "" || Number(value) === 0) return "-";
  return formatPrice(value, digits);
};

// Profit/Loss as money: +$1.06 (green), -$0.51 (red), $0.00
const renderProfitLoss = (value) => {
  if (value === null || value === undefined || value === "" || value === "-") return "-";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  const sign = num > 0 ? "+" : num < 0 ? "-" : "";
  return (
    <span className={`fw-semibold ${num > 0 ? "text-success" : num < 0 ? "text-danger" : ""}`}>
      {`${sign}$${Math.abs(num).toFixed(2)}`}
    </span>
  );
};

const renderTypeBadge = (type) => {
  const upper = String(type ?? "").toUpperCase();
  if (upper === "BUY") {
    return <span className="badge bg-success-subtle text-success px-2 py-1 font-size-12">BUY</span>;
  }
  if (upper === "SELL") {
    return <span className="badge bg-danger-subtle text-danger px-2 py-1 font-size-12">SELL</span>;
  }
  return type ?? "-";
};

export default function LiveTrades() {
  const handleError = useHandleError();
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
  const mid = sessionStorage.getItem("mid");

  const [loading, setLoading] = useState(false);
  const [tradeData, setTradeData] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [filters, setFilters] = useState({
    accountType: DEFAULT_ACCOUNT_TYPE,
    account: "",
  });
  // true while the socket is delivering trades (API auto refresh is paused during that time)
  const [socketLive, setSocketLive] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  // Account whose trades are in the table right now ("" = nothing loaded, auto refresh is off)
  const [activeAccount, setActiveAccount] = useState("");
  // Only the newest request may update the table (older, slower responses are ignored)
  const latestRequestRef = useRef(0);

  const clearFilters = () => {
    latestRequestRef.current += 1;
    setFilters({
      accountType: DEFAULT_ACCOUNT_TYPE,
      account: "",
    });
    setSearchTerm("");
    setSearchQuery("");
    setTradeData([]);
    setActiveAccount("");
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === "accountType") {
      // Stop refreshing the previous account type's trades
      latestRequestRef.current += 1;
      setActiveAccount("");
      setTradeData([]);
      setFilters((prev) => ({ ...prev, accountType: value, account: "" }));
      setAccounts([]);
      const list = await getAllAccounts(value);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters((prev) => ({ ...prev, accountType: value, account: firstAcc }));
      if (firstAcc) {
        fetchTradeData({
          accountType: value,
          account: firstAcc,
        });
      }
    }
  };

  const getAllAccounts = async (type) => {
    if (!type) return [];

    const url =
      type === "live"
        ? `${BASE_URL}/MT5M/real_accounts_number_list`
        : `${BASE_URL}/MT5M/demo_account_number_list`;

    try {
      setLoading(true);
      const res = type === "live"
        ? await fetchRealAccounts()
        : await axios.get(url, { headers: getApiHeaders() });

      if (res.data.code === 200) {
        const list = res.data.data || [];
        setAccounts(list);
        return list;
      } else {
        MessageToster("error", res.data.message || "Failed to fetch accounts");
        return [];
      }
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const formatTrade = (trade) => {
    const action = trade.Action !== undefined
      ? (Number(trade.Action) === 0 ? "Buy" : Number(trade.Action) === 1 ? "Sell" : String(trade.Action))
      : (trade.Type ?? "-");

    let openTime = trade.TimeCreate || trade.Open_Time || trade.TimeUpdate || "-";
    if (typeof openTime === "number") {
      openTime = new Date(openTime * 1000).toLocaleString();
    }

    const digits = trade.Digits;

    return {
      Account: trade.Account ?? trade.Login ?? "-",
      // Ticket is hidden from the table and export for now
      // Ticket: trade.Ticket ?? trade.Position ?? trade.ExpertPositionID ?? "-",
      Symbol: trade.Symbol ?? "-",
      Volume: trade.Volume ?? "-",
      Type: action,
      "Open Time": openTime,
      "Open price": formatPrice(trade.PriceOpen ?? trade.Open_price ?? trade.Open_Price, digits),
      "Current Price": formatPrice(trade.PriceCurrent ?? trade.current_price ?? trade.Current_Price, digits),
      "T/P": formatTpSl(trade.PriceTP ?? trade["T/P"], digits),
      "S/L": formatTpSl(trade.PriceSL ?? trade["S/L"], digits),
      Swap: trade.Storage ?? trade.Swap ?? "-",
      "Profit/Loss": trade.Profit ?? "-",
    };
  };

  // Calls the open trades API and returns the list of trades (empty list when there are none)
  const requestOpenTrades = async (account) => {
    const res = await axios.post(
      `${BASE_URL}/MT5M/my_trade_data?loginid=${Number(account)}&trade_type=open`,
      {},
      { headers: getApiHeaders() }
    );
    if (res.data.code === 200 && Array.isArray(res.data.data)) {
      return res.data.data.filter(isValidTrade);
    }
    return [];
  };

  // Manual load (page open, Search button, account type change): shows the loader and errors
  const fetchTradeData = async (filters) => {
    if (!filters.accountType) {
      return MessageToster("error", "Please select account type");
    }
    if (!filters.account) {
      return MessageToster("error", "Please select account number");
    }
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    setSearchQuery(searchTerm);
    setActiveAccount(filters.account);
    try {
      const trades = await requestOpenTrades(filters.account);
      if (requestId !== latestRequestRef.current) return;
      setTradeData(trades);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      setTradeData([]);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // Auto refresh: same API, but silent - no loader and no error popups every few seconds
  const refreshTradeData = async () => {
    if (!activeAccount) return;
    const requestId = ++latestRequestRef.current;
    try {
      const trades = await requestOpenTrades(activeAccount);
      if (requestId !== latestRequestRef.current) return;
      setTradeData(trades);
    } catch (error) {
      if (requestId !== latestRequestRef.current) return;
      if (error.response?.status === 401) {
        // Session expired: let the common handler log the user out
        handleError(error);
      } else if (error.response) {
        // Server answered with an error (e.g. no open trades): same result as a manual load
        setTradeData([]);
      }
      // Network problem: keep the trades already on screen and try again on the next refresh
    }
  };

  // API fallback: runs only while the socket is not delivering trades
  usePolling(
    refreshTradeData,
    LIVE_TRADES_REFRESH_MS,
    Boolean(activeAccount) && !loading && !socketLive
  );

  // Socket: open trades of the account shown in the table, pushed by the server
  useEffect(() => {
    if (!activeAccount || !LIVE_TRADES_SOCKET_URL) return;

    let ws = null;
    let stopped = false;
    let retryTimer = null;
    let silenceTimer = null;
    let retryDelay = LIVE_TRADES_SOCKET_RETRY_MS;

    const scheduleReconnect = () => {
      if (stopped) return;
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, retryDelay);
      retryDelay = Math.min(retryDelay * 2, LIVE_TRADES_SOCKET_RETRY_MAX_MS);
    };

    function connect() {
      if (stopped) return;

      try {
        ws = new WebSocket(LIVE_TRADES_SOCKET_URL);
      } catch (error) {
        scheduleReconnect();
        return;
      }

      const socket = ws;

      // Server starts sending open trades once it gets the account number
      socket.onopen = () => {
        if (stopped || socket !== ws) return;
        socket.send(JSON.stringify({ login: Number(activeAccount) }));
      };

      socket.onmessage = (event) => {
        if (stopped || socket !== ws) return;
        const trades = parseSocketTrades(event.data, activeAccount);
        if (!trades) return;

        // Socket data is newer than any API response still on its way
        latestRequestRef.current += 1;
        setTradeData(trades);
        setSocketLive(true);
        retryDelay = LIVE_TRADES_SOCKET_RETRY_MS;

        // No message for a while = socket is stuck: hand over to the API until it speaks again
        clearTimeout(silenceTimer);
        silenceTimer = setTimeout(() => {
          if (!stopped) setSocketLive(false);
        }, LIVE_TRADES_SOCKET_SILENCE_MS);
      };

      socket.onerror = () => {
        // "onclose" always follows and does the fallback + reconnect
      };

      socket.onclose = () => {
        if (stopped || socket !== ws) return;
        clearTimeout(silenceTimer);
        setSocketLive(false);
        scheduleReconnect();
      };
    }

    connect();

    return () => {
      stopped = true;
      clearTimeout(retryTimer);
      clearTimeout(silenceTimer);
      setSocketLive(false);
      if (ws) ws.close();
    };
  }, [activeAccount]);

  useEffect(() => {
    const initPage = async () => {
      const list = await getAllAccounts(DEFAULT_ACCOUNT_TYPE);
      const firstAcc = list && list.length > 0 ? list[0] : "";
      setFilters((prev) => ({
        ...prev,
        accountType: DEFAULT_ACCOUNT_TYPE,
        account: firstAcc,
      }));
      if (firstAcc) {
        fetchTradeData({
          accountType: DEFAULT_ACCOUNT_TYPE,
          account: firstAcc,
        });
      }
    };
    initPage();
  }, []);

  const formattedTradeData = (Array.isArray(tradeData) ? tradeData : []).map(formatTrade);
  const filteredData = formattedTradeData.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.Account || "").toLowerCase().includes(query) ||
      String(item.Symbol || "").toLowerCase().includes(query) ||
      String(item.Type || "").toLowerCase().includes(query) ||
      String(item["Open Time"] || "").toLowerCase().includes(query) ||
      String(item["Open price"] || "").toLowerCase().includes(query) ||
      String(item["Current Price"] || "").toLowerCase().includes(query) ||
      String(item["T/P"] || "").toLowerCase().includes(query) ||
      String(item["S/L"] || "").toLowerCase().includes(query) ||
      String(item.Swap || "").toLowerCase().includes(query) ||
      String(item["Profit/Loss"] || "").toLowerCase().includes(query)
    );
  });

  // Exported sheet = the rows shown in the table (Profit/Loss stays a number so Excel can sum it)
  const getExportRows = () =>
    filteredData.map(({ "Profit/Loss": profit, ...rest }) => ({
      ...rest,
      "Profit/Loss ($)": profit,
    }));

  return (
    <>
      <div id="layout-wrapper">
        {loading && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">Live Trades</h2>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            {/* <option value="">Account type</option> */}
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            {/* <option value="demo">Demo</option> */}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account Number</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account No" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                            {/* {accounts.map((acc) => (
                                                            <option key={acc} value={acc}>
                                                                {acc}
                                                            </option>
                                                        ))} */}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light w-100"
                            onClick={() => fetchTradeData(filters)}
                          >
                            Search
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={clearFilters}
                            disabled={!filters.account && !searchTerm}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="ms-auto">
                          <ExportButton
                            disabled={!filteredData || filteredData.length === 0}
                            onClick={() => {
                              exportToExcel(
                                getExportRows(),
                                "Live_Trade",
                                "default"
                              );
                            }}
                          />
                        </div>
                      </div>

                      <MobileFilter
                        title="History"
                        onExport={() => {
                          exportToExcel(
                            getExportRows(),
                            "Live_Trade",
                            "default"
                          );
                        }}
                        exportDisabled={!filteredData || filteredData.length === 0}
                      >
                        <div>
                          <label htmlFor="">Account Type</label>
                          <select
                            className="form-select"
                            name="accountType"
                            value={filters.accountType}
                            onChange={handleChange}
                          >
                            <option value="" disabled hidden>
                              Select Account Type
                            </option>
                            <option value="live">Live</option>
                            {/* <option value="demo">Demo</option> */}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Account Number</label>
                          <select
                            className="form-select"
                            name="account"
                            value={filters.account}
                            onChange={handleChange}
                            disabled={!filters.accountType}
                          >
                            <option value="" disabled hidden>
                              {accounts?.length > 0 ? "Select Account No" : "No Account Found"}
                            </option>
                            {accounts?.length > 0 ? (
                              accounts.map((acc) => (
                                <option key={acc} value={acc}>
                                  {acc}
                                </option>
                              ))
                            ) : (
                              <option disabled>No accounts available</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            type="search"
                            className="form-control"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={() => fetchTradeData(filters)}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={clearFilters}
                            disabled={!filters.account && !searchTerm}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>

                      <CommonTable
                        columns={[
                          { header: "Account", accessor: "Account" },
                          { header: "Symbol", accessor: "Symbol" },
                          { header: "Volume", accessor: "Volume" },
                          { header: "Type", render: (row) => renderTypeBadge(row.Type) },
                          { header: "Open Time", accessor: "Open Time" },
                          { header: "Open price", accessor: "Open price" },
                          { header: "Current Price", accessor: "Current Price" },
                          { header: "T/P", accessor: "T/P" },
                          { header: "S/L", accessor: "S/L" },
                          { header: "Swap", accessor: "Swap" },
                          { header: "Profit/Loss", render: (row) => renderProfitLoss(row["Profit/Loss"]) },
                        ]}
                        data={filteredData}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>{" "}
                {/* end col */}
              </div>
            </div>
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}
